import React from 'react';
import { cn } from '@/lib/utils';

interface CompetitionIconProps {
  competition: string;
  className?: string;
  appearance?: CompetitionIconAppearance;
}

export type CompetitionIconAppearance = 'raw' | 'framed' | 'softened';

interface FlagIconProps {
  className?: string;
  appearance?: CompetitionIconAppearance;
}

const FLAG_SVG_CLASS_NAME = 'block h-full w-full';

const getFlagWhite = (appearance: CompetitionIconAppearance) =>
  appearance === 'softened' ? '#EEF3F8' : '#FFFFFF';

const FlagShell = ({
  className,
  appearance = 'softened',
  children,
}: React.PropsWithChildren<FlagIconProps>) => {
  if (appearance === 'raw') {
    return <span className={cn('inline-flex shrink-0 align-middle', className)}>{children}</span>;
  }

  return (
    <span
      className={cn(
        'inline-flex shrink-0 overflow-hidden align-middle',
        appearance === 'framed'
          ? 'rounded-[0.35rem] bg-slate-950/72 p-px ring-1 ring-white/10 shadow-[0_1px_0_rgba(255,255,255,0.06)]'
          : 'rounded-[0.35rem] bg-slate-950/78 p-px ring-1 ring-slate-200/12 shadow-[0_2px_10px_rgba(2,6,23,0.28),inset_0_1px_0_rgba(255,255,255,0.04)]',
        className
      )}
    >
      {children}
    </span>
  );
};

export const TrophyIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    className={className}
  >
    {/* Improved trophy with better gold gradient */}
    <defs>
      <linearGradient id="goldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#FFC342" />
        <stop offset="50%" stopColor="#D4AF37" />
        <stop offset="100%" stopColor="#AA8C2C" />
      </linearGradient>
    </defs>
    
    {/* Trophy body with gradient */}
    <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" fill="url(#goldGradient)" stroke="#AA8C2C" strokeWidth="1.5" />
    
    {/* Trophy handles with gradient */}
    <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" stroke="#D4AF37" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" stroke="#D4AF37" strokeWidth="1.5" strokeLinecap="round" />
    
    {/* Trophy base */}
    <rect x="10" y="18" width="4" height="4" fill="url(#goldGradient)" stroke="#AA8C2C" strokeWidth="1.5" />
    <path d="M8 22h8" stroke="#AA8C2C" strokeWidth="1.5" strokeLinecap="round" />
    
    {/* Trophy stems */}
    <path d="M10 14.5V17c0 .55-.47.98-.97 1.21" stroke="#D4AF37" strokeWidth="1.5" />
    <path d="M14 14.5V17c0 .55.47.98.97 1.21" stroke="#D4AF37" strokeWidth="1.5" />
    
    {/* Highlight detail */}
    <path d="M9 4h6M9 6h6" stroke="#FFC342" strokeWidth="0.75" strokeLinecap="round" />
  </svg>
);

export const EnglandFlagIcon: React.FC<FlagIconProps> = ({ className, appearance = 'softened' }) => (
  <FlagShell className={className} appearance={appearance}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 16"
      fill="none"
      className={FLAG_SVG_CLASS_NAME}
    >
      <rect width="24" height="16" fill={getFlagWhite(appearance)} />
      <path d="M0 8h24M12 0v16" stroke="#CF142B" strokeWidth="3" />
    </svg>
  </FlagShell>
);

export const SpainFlagIcon: React.FC<FlagIconProps> = ({ className, appearance = 'softened' }) => (
  <FlagShell className={className} appearance={appearance}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 16"
      fill="none"
      className={FLAG_SVG_CLASS_NAME}
    >
      <rect width="24" height="16" fill="#FFC400" />
      <rect y="0" width="24" height="4" fill="#C60B1E" />
      <rect y="12" width="24" height="4" fill="#C60B1E" />
      <g transform="translate(5,5.5) scale(0.05)">
        <path d="M0,0 H150 V100 H0 Z" fill="#C60B1E" />
        <path d="M37.5,25 h75 v50 h-75 z" fill="#FFDF00" />
        <path d="M37.5,25 h18.75 v50 h-18.75 z" fill="#C60B1E" />
        <path d="M93.75,25 h18.75 v50 h-18.75 z" fill="#C60B1E" />
        <path d="M63,35 A1.5,1.5 0 0,1 87,35 A1.5,1.5 0 0,1 63,35" fill="#C60B1E" />
        <path d="M65,60 A1.2,1.2 0 0,0 85,60 A1.2,1.2 0 0,0 65,60" fill="#C60B1E" />
      </g>
    </svg>
  </FlagShell>
);

export const ItalyFlagIcon: React.FC<FlagIconProps> = ({ className, appearance = 'softened' }) => (
  <FlagShell className={className} appearance={appearance}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 16"
      fill="none"
      className={FLAG_SVG_CLASS_NAME}
    >
      <rect width="8" height="16" fill="#008C45" />
      <rect x="8" width="8" height="16" fill={getFlagWhite(appearance)} />
      <rect x="16" width="8" height="16" fill="#CD212A" />
    </svg>
  </FlagShell>
);

export const FranceFlagIcon: React.FC<FlagIconProps> = ({ className, appearance = 'softened' }) => (
  <FlagShell className={className} appearance={appearance}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 16"
      fill="none"
      className={FLAG_SVG_CLASS_NAME}
    >
      <rect width="8" height="16" fill="#002654" />
      <rect x="8" width="8" height="16" fill={getFlagWhite(appearance)} />
      <rect x="16" width="8" height="16" fill="#ED2939" />
    </svg>
  </FlagShell>
);

export const GermanyFlagIcon: React.FC<FlagIconProps> = ({ className, appearance = 'softened' }) => (
  <FlagShell className={className} appearance={appearance}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 16"
      fill="none"
      className={FLAG_SVG_CLASS_NAME}
    >
      <rect width="24" height="5.33" fill="#000000" />
      <rect y="5.33" width="24" height="5.33" fill="#DD0000" />
      <rect y="10.66" width="24" height="5.33" fill="#FFCE00" />
    </svg>
  </FlagShell>
);

export const PortugalFlagIcon: React.FC<FlagIconProps> = ({ className, appearance = 'softened' }) => (
  <FlagShell className={className} appearance={appearance}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 16"
      fill="none"
      className={FLAG_SVG_CLASS_NAME}
    >
      <rect width="9" height="16" fill="#006600" />
      <rect x="9" width="15" height="16" fill="#FF0000" />
      <circle cx="9" cy="8" r="4.8" fill="#FFFF00" />
      <circle cx="9" cy="8" r="4" fill="#FF0000" />
      <path d="M6 6.5 h6 v3 h-6 z" fill="#002D72" />
    </svg>
  </FlagShell>
);

export const BrazilFlagIcon: React.FC<FlagIconProps> = ({ className, appearance = 'softened' }) => (
  <FlagShell className={className} appearance={appearance}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 16"
      fill="none"
      className={FLAG_SVG_CLASS_NAME}
    >
      <rect width="24" height="16" fill="#009B3A" />
      <path d="M12 2.2 20.4 8 12 13.8 3.6 8 12 2.2Z" fill="#FFDF00" />
      <circle cx="12" cy="8" r="2.9" fill="#002776" />
    </svg>
  </FlagShell>
);

const CompetitionIcon: React.FC<CompetitionIconProps> = ({
  competition,
  className = '',
  appearance = 'softened',
}) => {
  switch (competition) {
    case 'All':
    case 'Champions League':
    case 'UEFA Champions League':
    case 'Copa Libertadores':
    case 'UEFA':
    case 'UEFA Euro':
      return <TrophyIcon className={className} />;
    case 'Premier League':
      return <EnglandFlagIcon className={className} appearance={appearance} />;
    case 'La Liga':
      return <SpainFlagIcon className={className} appearance={appearance} />;
    case 'Serie A':
      return <ItalyFlagIcon className={className} appearance={appearance} />;
    case 'Ligue 1':
      return <FranceFlagIcon className={className} appearance={appearance} />;
    case 'Bundesliga':
      return <GermanyFlagIcon className={className} appearance={appearance} />;
    case 'Primeira Liga':
      return <PortugalFlagIcon className={className} appearance={appearance} />;
    case 'Brasileirao Serie A':
      return <BrazilFlagIcon className={className} appearance={appearance} />;
    default:
      return <TrophyIcon className={className} />;
  }
};

export default CompetitionIcon; 
